import { PERSONALIZED_PRODUCTS, CONTACT } from "@/lib/products";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { trackWhatsAppClick } from "@/lib/analytics";

const orderProductOnWhatsApp = (p) => {
  trackWhatsAppClick({ placement: "personalize_card", productId: p.id });
  const msg = [
    `Hi Shine on Coin, I'd like to order:`,
    `*${p.name}*`,
    p.designLabel ? `Style: ${p.designLabel}` : null,
    "",
    "Please share pricing and next steps.",
  ]
    .filter(Boolean)
    .join("\n");
  window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
};

// Every product's image/title links to its own category's catalog page.
const catalogHrefFor = (p) => `/catalog?type=${p.productType}`;

export default function PersonalizedSection({ onCustomize }) {
  return (
    <section id="personalize" className="relative pt-6 md:pt-10 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs tracking-widest-2 uppercase text-[#d4af37]">Section 01 · Personalize</div>
          <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white">
            Craft a keepsake,
            <span className="silver-gradient-text italic"> exactly your way.</span>
          </h2>
          <p className="mt-5 text-white/70 text-base md:text-lg">
            Nine keepsakes in 99.9 silver and 18–22 ct gold — from a daily-wear gold
            nameplate stretch bracelet and a wearable gold photo locket, to a light 5 g
            floating coin, 10 g / 20 g / 30 g coins in round or square acrylic frames,
            our signature flower-CZ coin, a premium multi-coin arched momento, and a
            regal 100 g silver plate momento. Upload a photograph, add a few words,
            we'll do the rest.
            {" "}{CONTACT.turnaround}
          </p>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-8">
          {PERSONALIZED_PRODUCTS.map((p, idx) => {
            return (
            <div
              key={p.id}
              className={`group relative text-left glass glass-hover rounded-3xl overflow-hidden transition duration-500 fade-up fade-up-delay-${Math.min(idx, 3)}`}
              data-testid={`personalized-card-${p.id}`}
            >
              <Link
                to={catalogHrefFor(p)}
                data-testid={`personalized-card-image-${p.id}`}
                className="block w-full text-left"
              >
                <div className="relative aspect-square overflow-hidden bg-black">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 text-[10px] tracking-widest-2 uppercase text-[#d4af37] glass px-3 py-1 rounded-full border border-[#d4af37]/40">
                    {p.tag}
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center border border-white/10 group-hover:bg-[#d4af37] group-hover:text-black transition">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </Link>
              <div className="p-5 md:p-8">
                <Link
                  to={catalogHrefFor(p)}
                  className="block text-left w-full"
                  data-testid={`personalized-card-title-${p.id}`}
                >
                  <div className="font-display text-2xl sm:text-2xl md:text-3xl text-white break-words leading-tight">{p.name}</div>
                  <p className="mt-3 text-sm md:text-base text-white/60 leading-relaxed line-clamp-4 md:line-clamp-none">{p.blurb}</p>
                </Link>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {Object.values(p.options)[0]?.slice(0, 4).map((v) => (
                    <span key={v} className="text-[11px] uppercase tracking-widest-2 text-white/70 border border-white/15 rounded-full px-3 py-1">
                      {v}
                    </span>
                  ))}
                </div>
                <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => orderProductOnWhatsApp(p)}
                    data-testid={`personalized-card-whatsapp-${p.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 bg-[#25D366] text-black text-sm font-medium hover:brightness-110 transition"
                  >
                    <MessageCircle size={15} /> Order via WhatsApp
                  </button>
                  <button
                    onClick={() => onCustomize?.(p)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3 border border-white/15 glass glass-hover text-white/80 text-xs tracking-widest-2 uppercase transition"
                    data-testid={`personalized-card-cta-${p.id}`}
                  >
                    Customise →
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
